import type { KeyboardEvent, ReactNode, SyntheticEvent } from 'react';
import { Text } from '@cowbell/ui';
import { StatusBadge } from '../../pages/moo/studio-components';
import ClampedText from './ClampedText';
import ConnectorLogo from './ConnectorLogo';
import type { StatusBadgeValue } from './connectors.view';
import styles from './Connectors.module.css';

interface ConnectorCardProps {
  readonly name: string;
  readonly connectorType: string;
  readonly description?: string;
  /** A line under the name, such as when the connection last synced. */
  readonly subtitle?: string;
  readonly logoUrl?: string;
  /** Sets the tile apart from its neighbours, such as an AI connector among security ones. */
  readonly tag?: string;
  readonly status?: StatusBadgeValue;
  readonly onSelect?: () => void;
  /** The tile's controls, top right. */
  readonly actions?: ReactNode;
}

const stop = (event: SyntheticEvent) => event.stopPropagation();

/**
 * Every connector tile, connected or available. The logo, name and actions
 * share a header row, the description runs the full width under it, and a
 * status, when there is one, sits on the tile's bottom edge.
 */
export default function ConnectorCard({
  name,
  connectorType,
  description,
  subtitle,
  logoUrl,
  tag,
  status,
  onSelect,
  actions,
}: ConnectorCardProps) {
  const interactive = onSelect
    ? {
        role: 'button' as const,
        tabIndex: 0,
        onClick: onSelect,
        onKeyDown: (event: KeyboardEvent) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onSelect();
          }
        },
      }
    : {};

  return (
    <div
      className={[
        styles.tile,
        onSelect && styles.interactive,
        tag && styles.tagged,
      ]
        .filter(Boolean)
        .join(' ')}
      {...interactive}
    >
      {tag && <span className={styles.ribbon}>{tag}</span>}
      <div className={styles.tileHeader}>
        <span className={styles.logo}>
          <ConnectorLogo connectorType={connectorType} logoUrl={logoUrl} />
        </span>
        <div className={styles.titleGroup}>
          <ClampedText fw={600} size="md" lineClamp={2}>
            {name}
          </ClampedText>
          {subtitle && (
            <Text size="xs" c="dimmed">
              {subtitle}
            </Text>
          )}
        </div>
        {actions && (
          // Controls act on their own, never also on the tile behind them.
          <div className={styles.tileActions} onClick={stop} onKeyDown={stop}>
            {actions}
          </div>
        )}
      </div>
      {description && (
        <ClampedText size="sm" c="dimmed" lineClamp={3}>
          {description}
        </ClampedText>
      )}
      {status && (
        <div className={styles.tileFooter}>
          <StatusBadge kind={status.kind} label={status.label} />
        </div>
      )}
    </div>
  );
}
